import { useState } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { PrimaryButton } from '../../../components/general/PrimaryButton.tsx';
import { SecondaryButton } from '../../../components/general/SecondaryButton.tsx';
import { MONTHS_NL_SHORT } from '../constants.ts';
import { cn } from '../../../lib/utils.ts';
import { fieldErrorClasses, fieldLabelClasses, fieldVariants } from '../../../components/general/fieldStyles.ts';

export type LineItemTransitoryScheduleValue = { start_month: string; end_month: string } | null;

const toMonthInput = (iso: string | null | undefined): string => (iso ? iso.slice(0, 7) : '');
const toFirstOfMonthIso = (monthInput: string): string => (monthInput ? `${monthInput}-01` : '');

const formatMonthLabel = (iso: string): string => {
    const [year, month] = iso.split('-');
    const monthName = MONTHS_NL_SHORT[Number(month) - 1] ?? month;
    return `${monthName} ${year}`;
};

type LineItemTransitoryScheduleFieldProps = {
    value?: LineItemTransitoryScheduleValue;
    onChange?: (value: LineItemTransitoryScheduleValue) => void;
    disabled?: boolean;
};

type TransitoryScheduleEditorProps = {
    value: LineItemTransitoryScheduleValue;
    onChange?: (value: LineItemTransitoryScheduleValue) => void;
    close: () => void;
};

function TransitoryScheduleEditor({ value, onChange, close }: TransitoryScheduleEditorProps) {
    const [start, setStart] = useState(toMonthInput(value?.start_month));
    const [end, setEnd] = useState(toMonthInput(value?.end_month));
    const startAfterEnd = Boolean(start && end && start > end);

    const apply = () => {
        if (!start || !end) {
            onChange?.(null);
            close();
            return;
        }
        if (startAfterEnd) return;
        onChange?.({ start_month: toFirstOfMonthIso(start), end_month: toFirstOfMonthIso(end) });
        close();
    };

    const clear = () => {
        onChange?.(null);
        close();
    };

    return (
        <>
            <div className="mb-1 text-sm font-semibold text-gray-900">Periode verdelen</div>
            <p className="mb-3 text-[11.5px] leading-4 text-gray-500">
                Verdeel deze boekingsregel over meerdere maanden.
            </p>
            <div className="grid grid-cols-1 gap-2.5">
                <label className={fieldLabelClasses}>
                    Van maand
                    <input
                        type="month"
                        value={start}
                        onChange={(event) => setStart(event.target.value)}
                        className={cn(fieldVariants(), "mt-1 normal-case tracking-normal")}
                    />
                </label>
                <label className={fieldLabelClasses}>
                    Tot en met maand
                    <input
                        type="month"
                        value={end}
                        onChange={(event) => setEnd(event.target.value)}
                        className={cn(fieldVariants(), "mt-1 normal-case tracking-normal")}
                    />
                </label>
                {startAfterEnd && (
                    <div role="alert" className={fieldErrorClasses}>
                        Startmaand moet vóór of gelijk aan de eindmaand liggen.
                    </div>
                )}
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
                <SecondaryButton title="Periode wissen" onClick={clear} />
                <PrimaryButton title="Toepassen" onClick={apply} disabled={startAfterEnd} />
            </div>
        </>
    );
}

export function LineItemTransitoryScheduleField({ value = null, onChange, disabled = false }: LineItemTransitoryScheduleFieldProps) {
    return (
        <Popover className="inline-flex">
            <PopoverButton
                disabled={disabled}
                title={value
                    ? `Transitorisch: ${formatMonthLabel(value.start_month)} – ${formatMonthLabel(value.end_month)}`
                    : 'Transitorische post instellen'}
                aria-label={value
                    ? `Transitorische post: ${formatMonthLabel(value.start_month)} tot ${formatMonthLabel(value.end_month)}`
                    : 'Transitorische post instellen'}
                className={cn(
                    'relative inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                    value
                        ? 'bg-primary/10 text-darkprimary hover:bg-primary/15'
                        : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700',
                )}
            >
                <CalendarDaysIcon className="h-4 w-4" aria-hidden="true" />
                {value && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />}
            </PopoverButton>

            <PopoverPanel
                anchor={{ to: 'bottom end', gap: 4 }}
                className="z-50 w-72 rounded-xl border border-gray-200 bg-white p-4 text-left shadow-xl"
            >
                {({ close }) => <TransitoryScheduleEditor value={value} onChange={onChange} close={close} />}
            </PopoverPanel>
        </Popover>
    );
}
